import { SportType } from '../../court/dto/court.dto';
import { GameRankedMode, GameType } from '../../game/dto/game.dto';

export enum AdminBookingPaymentMode {
  STRIPE_LINK = 'STRIPE_LINK',
  COMPED = 'COMPED',
}

// Capacity ceiling matches the WhatsApp tool executor's clamp at 20. Padel
// caps at 4 in practice; the upper bound is mostly a defense against
// pathological inputs (an operator typo of 99999, an attacker probing).
export const MAX_ADMIN_BOOKING_CAPACITY = 20;

export class CreateAdminBookingDto {
  organizationId: string;

  userId: string;

  courtId: number;

  // ISO-8601 strict — without this, "tomorrow" or "2026-13-99" pass the
  // string-not-empty check, then `new Date(s)` returns an Invalid Date,
  // then `startDate <= new Date()` is false (NaN comparisons), and the
  // service slips into pricing where it 500s with a confusing error.
  startTime: string;

  endTime: string;

  sportType: SportType;

  capacity?: number;

  gameType?: GameType;

  rankedMode?: GameRankedMode;

  paymentMode: AdminBookingPaymentMode;

  // Per-booking override for the Stripe Checkout expires_at window.
  // Falls back to Organization.bookingPaymentDeadlineMinutes when omitted.
  // Bounds match the org-level setter (and Stripe's hard limits): the API
  // requires at least 30 min in the future and at most 24 h; we cap at
  // 1430 to keep a 10-min buffer against clock drift. Ignored when
  // paymentMode === COMPED (no Stripe session, nothing to expire).
  paymentDeadlineMinutes?: number;

  // Per-game opt-in for court recording, copied onto the created Game.
  recordingEnabled?: boolean;

  // MYS-140 — where to email the booking (payment link, or the comped
  // confirmation) when the player is a GUEST: a guest has no app account, so
  // no push and only a synthetic placeholder address. Optional — without it
  // the operator shares the link themselves (copy / WhatsApp / SMS). Refused
  // for a registered player, whose own account email is always used.
  guestEmail?: string;
}

export type AdminBookingRecipientDto = {
  // True when the booked player is a guest (no app account, so no push).
  isGuest: boolean;
  // The address the booking email goes to: the account email for a
  // registered player, the operator-supplied `guestEmail` for a guest, null
  // when nobody will be emailed (the operator has to share the link).
  email: string | null;
  // The player's phone as stored, for the WhatsApp / SMS share links. Null
  // when none is on file.
  phoneNumber: string | null;
};

export type AdminBookingResponseDto = {
  // STRIPE_LINK fields
  blockadeId?: string;
  checkoutUrl?: string;
  expiresAt?: string;
  // COMPED fields
  gameId?: number;
  // MYS-140 — how the booking reaches the player, so the success view says
  // what actually happened. Optional so an older server keeps working.
  recipient?: AdminBookingRecipientDto;
};

export type AdminBookingPricePreviewDto = {
  totalAmount: number;
  applicationFees: number;
  currency: string;
  // False when the STRIPE_LINK path would refuse this booking outright: the
  // amount is zero/negative after discounts, or it lands inside Stripe's
  // 1-49 cent dead zone. The preview used to report `totalAmount: 0.45` with
  // no signal, so the dashboard offered a bookable price for a booking that
  // could not be created. Optional so a client built against the older shape
  // keeps compiling and behaving exactly as before.
  isChargeable?: boolean;
  // Stripe's minimum charge in MAJOR units (euros), for operator-facing copy.
  minimumChargeAmount?: number;
  // What `totalAmount` is a price OF: `per_player` (PUBLIC — the booker pays
  // one seat, joiners are charged the rest) or `whole_court` (PRIVATE). The
  // modal rendered a bare amount whose meaning flips with the capacity dropdown
  // beside it, so an operator quoting it over the phone understated the court
  // by a factor of `capacity`.
  //
  // Optional so a client built against the older shape keeps compiling and
  // behaving exactly as before (D4). Added by hand rather than by `dto:sync`,
  // which skips a file the client already has.
  payerAmountBasis?: 'per_player' | 'whole_court';
};
